import { useEffect, useRef, useState } from 'react';
import { FiUser } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import Axios from 'axios';
import moment from 'moment';
import Button from '~/components/button';
import Container from '~/components/container';
import Loading from '~/components/loading';
import RadioButton from '~/components/radioButton';
import { useAuth } from '~/hooks/contexts/useAuth';
import useApi, { ApiMethod } from '~/hooks/useApi';
import Endpoints from '~/services/endpoints';
import DateField from './DateField';
import PixModal, { NegotiationPix } from './PixModal';
import Constants from '~/utils/constants';
import { creditCardMask, currencyMask } from '~/utils/masks';
import { DebtNegotiationClientState } from '../ClientSearch';

import * as St from './styles';

interface DebtItem {
  codigo?: string;
  dataTransacao?: string;
  dataVencimento?: string;
  id: string;
  parcelaAtual?: number;
  qtdParcelasAPagar?: number;
  qtdParcelasPagas?: number;
  situacao?: string;
  totalParcelas?: number;
  valor?: number;
  valorComDesconto?: number;
  valorCompra?: number;
  valorDesconto?: number;
  valorPendente?: number;
  vencimento?: string;
}

interface DebtGroup {
  label: string;
  notice: string;
  items: DebtItem[];
}

interface DebtNegotiationDetail {
  cartaoTeucard?: {
    limiteCreditoUtilizado?: number;
    limiteEmprestimoUtilizado?: number;
    limiteExtraUtilizado?: number;
    limiteTotalUtilizado?: number;
    numeroCartao?: string;
    status?: string;
  };
  compras?: DebtItem[];
  comprasLimite12x?: DebtItem[];
  cpf?: string;
  elegivel?: boolean;
  emprestimos?: DebtItem[];
  financiamentos?: DebtItem[];
  fotoCliente?: string;
  idConsulta?: string;
  idFatura?: string;
  nome?: string;
  statusFatura?: string;
  valorTotalTransacoes?: number;
  valorTotalTransacoesComDesconto?: number;
  valorUltimaFatura?: number;
  vencimentoFatura?: string;
}

interface PaymentOption {
  idOpcaoPagamento: string;
  parcelas: number;
  taxaIOF?: number;
  valorComDesconto?: number;
  valorDesconto?: number;
  valorFinanciado?: number;
  valorIOF?: number;
  valorJuros?: number;
  valorOriginal?: number;
  valorParcela?: number;
  valorTotal?: number;
  vencimento?: string;
}

interface NegotiationCalculation {
  modalidade?: 'A_VISTA' | 'PARCELADO';
  opcoes?: PaymentOption[];
  percentualDesconto?: number;
  vencimento?: string;
}

const formatMoney = (value?: number) =>
  value === undefined || value === null ? '-' : `R$ ${currencyMask(value)}`;

const formatDate = (value?: string) =>
  value ? moment(value).format('DD/MM/YYYY') : '-';

const asDebtItems = (items?: unknown) =>
  Array.isArray(items) ? (items as DebtItem[]) : [];

const DEBT_COLLECTIONS: Record<string, Pick<DebtGroup, 'label' | 'notice'>> = {
  financiamentos: {
    label: 'Financiamento de faturas ativos',
    notice:
      'O financiamento de faturas será considerado no cálculo do acordo.',
  },
  emprestimos: {
    label: 'Financiamento de empréstimos ativos',
    notice:
      'O financiamento de empréstimo será considerado no cálculo do acordo.',
  },
  compras: {
    label: 'Financiamento de compras ativos',
    notice:
      'O financiamento de compras será considerado no cálculo do acordo.',
  },
  comprasLimite12x: {
    label: 'Financiamento de limite 12x ativos',
    notice:
      'O financiamento de limite 12x será considerado no cálculo do acordo.',
  },
};

const normalizeDetail = (payload: unknown): DebtNegotiationDetail | null => {
  if (!payload || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;
  const nested =
    record.data && typeof record.data === 'object'
      ? (record.data as Record<string, unknown>)
      : null;
  const source =
    record.cartaoTeucard || record.nome || record.cpf ? record : nested;

  if (!source) return null;

  return source as DebtNegotiationDetail;
};

const normalizeCalculation = (payload: unknown): NegotiationCalculation | null => {
  if (!payload || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;
  const nested =
    record.data && typeof record.data === 'object'
      ? (record.data as Record<string, unknown>)
      : null;
  const source = Array.isArray(record.opcoes) ? record : nested;

  if (!source || !Array.isArray(source.opcoes)) return null;

  return source as NegotiationCalculation;
};

type NegotiationType = 'avista' | 'parcelado';

const DISCOUNT_RANGE: Record<NegotiationType, { min: number; max: number }> = {
  avista: { min: 10, max: 30 },
  parcelado: { min: 10, max: 20 },
};

const DebtNegotiationClient = (): JSX.Element => {
  const history = useHistory<DebtNegotiationClientState>();
  const { user } = useAuth();
  const searchClient = history.location.state;
  const [detail, setDetail] = useState<DebtNegotiationDetail | null>(null);
  const [feedback, setFeedback] = useState('');
  const [newDueDate, setNewDueDate] = useState<Date | null>(null);
  const [negotiationType, setNegotiationType] = useState<NegotiationType>('avista');
  const [installments, setInstallments] = useState<number | ''>('');
  const [calculation, setCalculation] = useState<NegotiationCalculation | null>(
    null,
  );
  const [discount, setDiscount] = useState(DISCOUNT_RANGE.avista.max);
  const [discountText, setDiscountText] = useState(String(DISCOUNT_RANGE.avista.max));
  const discountLimits = DISCOUNT_RANGE[negotiationType];
  const getEligibleClient = useApi<DebtNegotiationDetail>(
    Endpoints.debtNegotiation.getEligibleClient,
    ApiMethod.POST,
  );
  const calculateNegotiation = useApi<NegotiationCalculation>(
    Endpoints.debtNegotiation.calculateNegotiation,
    ApiMethod.POST,
  );
  const audioInputRef = useRef<HTMLInputElement>(null);
  const [creatingNegotiation, setCreatingNegotiation] = useState(false);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [pix, setPix] = useState<NegotiationPix | null>(null);
  const [pixModalOpen, setPixModalOpen] = useState(false);
  const [showRequired, setShowRequired] = useState(false);
  const negotiationLocked = !!pix;
  const calculationTimer = useRef<number>();
  const requestCalculation = (
    nextType: NegotiationType,
    nextDiscount: number,
    nextDate: Date | null,
  ) => {
    const idConsulta = detail?.idConsulta;

    window.clearTimeout(calculationTimer.current);

    if (!idConsulta || !nextDate) return;

    calculationTimer.current = window.setTimeout(async () => {
      try {
        const response = await calculateNegotiation.callApi({
          idConsulta,
          modalidade: nextType === 'avista' ? 'A_VISTA' : 'PARCELADO',
          percentualDesconto: nextDiscount,
          vencimento: moment(nextDate).format('YYYY-MM-DD'),
        });

        if (!(response.status >= 200 && response.status < 300)) {
          setCalculation(null);
          setFeedback(
            response.mensagem || 'Não foi possível calcular a negociação.',
          );
          return;
        }

        const nextCalculation = normalizeCalculation(response.data);

        if (!nextCalculation) {
          setCalculation(null);
          setFeedback('O cálculo não retornou as opções de pagamento.');
          return;
        }

        setFeedback('');
        setCalculation(nextCalculation);
      } catch (error) {
        setCalculation(null);
        setFeedback(
          error instanceof Error
            ? error.message
            : 'Não foi possível calcular a negociação.',
        );
      }
    }, 300);
  };
  const updateDiscount = (
    nextValue: number,
    range: { min: number; max: number } = discountLimits,
  ) => {
    const clamped = Math.min(range.max, Math.max(range.min, nextValue));
    setDiscount(clamped);
    setDiscountText(String(clamped));
    requestCalculation(negotiationType, clamped, newDueDate);
  };

  const selectNegotiationType = (type: NegotiationType) => {
    const range = DISCOUNT_RANGE[type];
    const nextDiscount = Math.min(range.max, Math.max(range.min, discount));
    setNegotiationType(type);
    setDiscount(nextDiscount);
    setDiscountText(String(nextDiscount));
    requestCalculation(type, nextDiscount, newDueDate);
  };

  const handleDiscountInput = (rawValue: string) => {
    const digits = rawValue.replace(/\D/g, '').slice(0, 2);
    setDiscountText(digits);

    if (!digits) return;

    const nextValue = Number(digits);

    if (nextValue >= discountLimits.min && nextValue <= discountLimits.max) {
      setDiscount(nextValue);
      requestCalculation(negotiationType, nextValue, newDueDate);
    }
  };

  const handleCreateNegotiation = async () => {
    const idConsulta = detail?.idConsulta;
    const idOpcaoPagamento = selectedOption?.idOpcaoPagamento;
    const cpfOperador = String(user?.cpf || '').replace(/\D/g, '');
    const cpfCliente = String(searchClient?.cpf || detail?.cpf || '').replace(
      /\D/g,
      '',
    );

    if (!newDueDate || !audioFile) {
      setShowRequired(true);
      setFeedback('');
      return;
    }

    if (!idConsulta || !idOpcaoPagamento || !cpfOperador || !cpfCliente) {
      setFeedback(
        'Calcule a negociação antes de confirmar. A opção de pagamento ainda não foi escolhida.',
      );
      return;
    }

    const formData = new FormData();
    formData.append('idConsulta', idConsulta);
    formData.append('idOpcaoPagamento', idOpcaoPagamento);
    formData.append('cpfOperador', cpfOperador);
    formData.append('cpfCliente', cpfCliente);
    formData.append('arquivo', audioFile, audioFile.name);

    setCreatingNegotiation(true);

    try {
      const response = await Axios.post(
        Endpoints.debtNegotiation.createNegotiation,
        formData,
        {
          headers: {
            apikey: `token ${localStorage.getItem(Constants.TOKEN_KEY)}`,
            Authorization: `Bearer ${localStorage.getItem(Constants.TOKEN_BEARER)}`,
            'Id-Sessao': localStorage.getItem(Constants.SESSION_ID) || '',
            'Id-Sistema': `${process.env.REACT_APP_ID_SISTEMA}`,
            'Id-User': localStorage.getItem(Constants.USER_ID) || '',
          },
        },
      );

      if (!(response.status >= 200 && response.status < 300)) {
        setFeedback(
          response.data?.mensagem || 'Não foi possível criar a negociação.',
        );
        return;
      }

      const nextPix = response.data?.pix ?? response.data?.data?.pix;

      if (!nextPix?.pixCopiaECola && !nextPix?.qrCodeBase64) {
        setFeedback('A negociação foi criada, mas o Pix não foi retornado.');
        return;
      }

      setFeedback('');
      setPix(nextPix);
      setPixModalOpen(true);
    } catch (error) {
      const errorMessage = error?.response?.data?.mensagem;
      const responseMessage = errorMessage ? String(errorMessage) : '';

      setFeedback(responseMessage || 'Não foi possível criar a negociação.');
    } finally {
      setCreatingNegotiation(false);
    }
  };

  const commitDiscountInput = () => {
    const nextValue = Number(discountText);

    if (!discountText || Number.isNaN(nextValue)) {
      updateDiscount(discountLimits.min);
      return;
    }

    updateDiscount(nextValue);
  };

  const paymentOptions = Array.isArray(calculation?.opcoes)
    ? calculation.opcoes.filter((option) => option.parcelas > 0)
    : [];
  const installmentOptions = paymentOptions.map((option) => option.parcelas);
  const selectedOption =
    paymentOptions.find((option) => option.parcelas === installments) ??
    paymentOptions[0];
  const entryValue = selectedOption?.valorParcela;
  const installmentValue = selectedOption?.valorParcela;
  const appliedDiscount = selectedOption?.valorDesconto;
  const debtTotal = selectedOption?.valorOriginal;
  const newInvoiceValue = selectedOption?.valorTotal;

  useEffect(() => {
    if (installmentOptions.length === 0) {
      setInstallments('');
      return;
    }

    setInstallments((current) =>
      typeof current === 'number' && installmentOptions.includes(current)
        ? current
        : installmentOptions[0],
    );
  }, [calculation]);

  useEffect(() => {
    const loadDetail = async () => {
      const preloadedDetail = normalizeDetail(searchClient?.eligibleDetail);

      if (preloadedDetail) {
        setDetail(preloadedDetail);
        return;
      }

      const cpfOperador = String(user?.cpf || '').replace(/\D/g, '');

      if (!searchClient?.cpf || !cpfOperador) {
        if (!searchClient?.cpf) {
          setFeedback('Cliente não informado para a consulta.');
        }
        return;
      }

      try {
        const response = await getEligibleClient.callApi({
          cpf: searchClient.cpf.replace(/\D/g, ''),
          cpfOperador,
        });

        if (!(response.status >= 200 && response.status < 300)) {
          setFeedback(
            response.mensagem || 'Não foi possível consultar o cliente.',
          );
          return;
        }

        const nextDetail = normalizeDetail(response.data);

        if (!nextDetail) {
          setFeedback('A consulta não retornou os dados do cliente.');
          return;
        }

        setDetail(nextDetail);
      } catch (error) {
        setFeedback(
          error instanceof Error
            ? error.message
            : 'Não foi possível consultar o cliente.',
        );
      }
    };

    loadDetail();
  }, [searchClient?.cpf, user?.cpf]);

  useEffect(
    () => () => {
      window.clearTimeout(calculationTimer.current);
    },
    [],
  );

  const currentInvoice = detail?.valorUltimaFatura;
  const futureInvoices =
    detail?.valorTotalTransacoes != null && currentInvoice != null
      ? Number((detail.valorTotalTransacoes - currentInvoice).toFixed(2))
      : undefined;
  const debtSummaryTotal =
    currentInvoice != null && futureInvoices != null
      ? Number((currentInvoice + futureInvoices).toFixed(2))
      : undefined;
  const card = detail?.cartaoTeucard;
  const debtGroups: DebtGroup[] = detail
    ? Object.entries(detail).flatMap(([key, value]) => {
        const collection = DEBT_COLLECTIONS[key];
        const items = asDebtItems(value);

        if (!collection || items.length === 0) return [];

        return [{ ...collection, items }];
      })
    : [];
  const cardNumber = card?.numeroCartao || searchClient?.numeroCartao;

  const fields = [
    { label: 'Nome', value: detail?.nome || searchClient?.nome || '-' },
    {
      label: 'Cartão',
      value: cardNumber ? creditCardMask(cardNumber).masked : '-',
    },
    {
      label: 'Status do cartão',
      value: card?.status || searchClient?.statusCartao || '-',
    },
    {
      label: 'Limite total utilizado',
      value: formatMoney(
        card?.limiteTotalUtilizado ?? searchClient?.limiteTotalUtilizado,
      ),
    },
    {
      label: 'Limite de compras',
      value: formatMoney(
        card?.limiteCreditoUtilizado ?? searchClient?.limiteCreditoUtilizado,
      ),
    },
    {
      label: 'Limite de empréstimo',
      value: formatMoney(
        card?.limiteEmprestimoUtilizado ??
          searchClient?.limiteEmprestimoUtilizado,
      ),
    },
    {
      label: 'Limite extra 12x',
      value: formatMoney(
        card?.limiteExtraUtilizado ?? searchClient?.limiteExtraUtilizado,
      ),
    },
    {
      label: 'Limite total',
      value: formatMoney(
        card?.limiteTotalUtilizado ??
          searchClient?.limiteTotal ??
          searchClient?.limiteTotalUtilizado,
      ),
    },
  ];

  return (
    <Container>
      <Loading
        status={getEligibleClient.loading || calculateNegotiation.loading}
      />
      {feedback && <St.Feedback>{feedback}</St.Feedback>}
      <PixModal
        open={pixModalOpen && !!pix}
        pix={pix}
        onClose={() => setPixModalOpen(false)}
      />
      <St.BoxCard>
        <St.Summary>
          <St.Avatar>
            {detail?.fotoCliente ? (
              <img src={detail.fotoCliente} alt={detail.nome || 'Cliente'} />
            ) : (
              <FiUser />
            )}
          </St.Avatar>
          <St.Fields>
            {fields.map((field) => (
              <St.Field key={field.label}>
                <St.Label>{field.label}</St.Label>
                <St.Value>{field.value}</St.Value>
              </St.Field>
            ))}
          </St.Fields>
        </St.Summary>
      </St.BoxCard>

      <St.Boxes>
        <St.SideColumn>
          <St.Panel>
            <St.PanelTitle>Resumo financeiro</St.PanelTitle>
            <St.FinanceSections>
              <St.FinanceSection>
                <St.SectionTitle>Última fatura (Atual)</St.SectionTitle>
                <St.PlainText>
                  Vencimento 
                  {' '}
                  {formatDate(detail?.vencimentoFatura)}
                </St.PlainText>
                <St.PlainText>Valor total</St.PlainText>
                <St.Amount>{formatMoney(currentInvoice)}</St.Amount>
                <St.Status>
                  <St.StatusDot />
                  {detail?.statusFatura || '-'}
                </St.Status>
              </St.FinanceSection>
              <St.FinanceSection>
                <St.SectionTitle>Faturas futuras</St.SectionTitle>
                <St.PlainText>Próximas faturas</St.PlainText>
                <St.PlainText>Valor total</St.PlainText>
                <St.Amount>{formatMoney(futureInvoices)}</St.Amount>
              </St.FinanceSection>
              <St.FinanceSection>
                <St.SectionTitle>Total da dívida</St.SectionTitle>
                <St.PlainText>Atual + futuras</St.PlainText>
                <St.PlainText>Valor total</St.PlainText>
                <St.Amount>{formatMoney(debtSummaryTotal)}</St.Amount>
                <St.SectionNote>
                  {formatMoney(currentInvoice)}
                  {' '}
                  +
                  {formatMoney(futureInvoices)}
                </St.SectionNote>
              </St.FinanceSection>
            </St.FinanceSections>
          </St.Panel>
          {debtGroups.length === 0 ? (
            <St.Panel $compact>
              <St.MutedText>Não possui dívidas.</St.MutedText>
            </St.Panel>
          ) : (
            <St.DebtColumn>
              {debtGroups.map((group) => (
                <St.DebtPanel key={group.label} $compact>
                  <St.DebtType>{group.label}</St.DebtType>
                  {group.items.map((item) => (
                    <St.DebtCard key={item.id}>
                      <St.DebtFields>
                        <St.FinanceSection>
                          <St.MutedText>Número do financiamento</St.MutedText>
                          <St.PlainText>{item.codigo || '-'}</St.PlainText>
                        </St.FinanceSection>
                        <St.FinanceSection>
                          <St.MutedText>Saldo devedor</St.MutedText>
                          <St.PlainText>
                            {formatMoney(item.valor)}
                          </St.PlainText>
                        </St.FinanceSection>
                        <St.FinanceSection>
                          <St.MutedText>
                            Saldo devedor após antecipação
                          </St.MutedText>
                          <St.PlainText>
                            {formatMoney(item.valorComDesconto)}
                          </St.PlainText>
                        </St.FinanceSection>
                        <St.FinanceSection>
                          <St.MutedText>Parcelas</St.MutedText>
                          <St.PlainText>
                            {item.parcelaAtual ?? '-'}
                            /
                            {item.totalParcelas ?? '-'}
                          </St.PlainText>
                        </St.FinanceSection>
                        <St.FinanceSection>
                          <St.MutedText>Vencimento</St.MutedText>
                          <St.PlainText>
                            {formatDate(item.dataVencimento || item.vencimento)}
                          </St.PlainText>
                        </St.FinanceSection>
                      </St.DebtFields>
                    </St.DebtCard>
                  ))}
                  <St.DebtNoticeRow>
                    <St.InfoNotice>
                      <St.InfoBadge>i</St.InfoBadge>
                      <span>{group.notice}</span>
                    </St.InfoNotice>
                    <St.FooterButton>
                      <Button outlined fitContent>
                        <span>Ver detalhes</span>
                      </Button>
                    </St.FooterButton>
                  </St.DebtNoticeRow>
                </St.DebtPanel>
              ))}
            </St.DebtColumn>
          )}
        </St.SideColumn>
        <St.SideColumn>
          <St.Panel>
            <St.ConfigStack>
              <div>
                <St.PanelTitle>Configurar negociação</St.PanelTitle>
                <St.ConfigBlock>
                  <St.EntryColumn>
                    <St.PlainText>Vencimento *</St.PlainText>
                    <DateField
                      value={newDueDate}
                      minDate={moment().startOf('day').toDate()}
                      maxDate={moment().startOf('day').add(15, 'days').toDate()}
                      disabled={negotiationLocked}
                      onChange={(date) => {
                        setNewDueDate(date);
                        requestCalculation(negotiationType, discount, date);
                      }}
                    />
                    {showRequired && !newDueDate && (
                      <St.RequiredError>
                        Informe a data de vencimento.
                      </St.RequiredError>
                    )}
                  </St.EntryColumn>
                  <St.Subtitle>Tipo de configuração</St.Subtitle>
                  <St.TypeList>
                    <St.RadioRow>
                      <St.RadioOption $disabled={negotiationLocked}>
                        <RadioButton
                          name="negotiationType"
                          checked={negotiationType === 'avista'}
                          disabled={negotiationLocked}
                          onChange={() => selectNegotiationType('avista')}
                        />
                        <span>À vista</span>
                      </St.RadioOption>
                      <St.DiscountChip>
                        Desconto 
                        {' '}
                        {DISCOUNT_RANGE.avista.max}
                        %
                      </St.DiscountChip>
                    </St.RadioRow>
                    <St.RadioRow>
                      <St.RadioOption $disabled={negotiationLocked}>
                        <RadioButton
                          name="negotiationType"
                          checked={negotiationType === 'parcelado'}
                          disabled={negotiationLocked}
                          onChange={() => selectNegotiationType('parcelado')}
                        />
                        <span>Parcelado</span>
                      </St.RadioOption>
                      <St.DiscountChip>
                        Desconto 
                        {' '}
                        {DISCOUNT_RANGE.parcelado.max}
                        %
                      </St.DiscountChip>
                    </St.RadioRow>
                  </St.TypeList>
                  <St.RangeRow>
                    <St.RangeBlock>
                      <St.Range
                        type="range"
                        min={discountLimits.min}
                        max={discountLimits.max}
                        value={discount}
                        disabled={negotiationLocked}
                        onChange={(event) =>
                          updateDiscount(Number(event.target.value))
                        }
                      />
                      <St.RangeLimits>
                        <St.RangeLimit>
                          {discountLimits.min}
                          %
                        </St.RangeLimit>
                        <St.RangeLimit>
                          {discountLimits.max}
                          %
                        </St.RangeLimit>
                      </St.RangeLimits>
                    </St.RangeBlock>
                    <St.PercentField
                      value={discountText}
                      inputMode="numeric"
                      disabled={negotiationLocked}
                      onChange={(event) =>
                        handleDiscountInput(event.target.value)
                      }
                      onBlur={commitDiscountInput}
                    />
                    <St.PercentSuffix>%</St.PercentSuffix>
                  </St.RangeRow>
                  <St.SummaryTabs>
                    <St.SummaryTab
                      type="button"
                      $active={negotiationType === 'avista'}
                      disabled={negotiationLocked}
                      onClick={() => selectNegotiationType('avista')}
                    >
                      À vista
                    </St.SummaryTab>
                    <St.SummaryTab
                      type="button"
                      $active={negotiationType === 'parcelado'}
                      disabled={negotiationLocked}
                      onClick={() => selectNegotiationType('parcelado')}
                    >
                      Parcelado
                    </St.SummaryTab>
                  </St.SummaryTabs>
                  <St.EntryRow>
                    <St.EntryColumn>
                      <St.EntryLabel>Valor da entrada</St.EntryLabel>
                      <St.Amount>{formatMoney(entryValue)}</St.Amount>
                    </St.EntryColumn>
                    <St.EntryColumn $end>
                      <St.EntryLabel>Entrada (%)</St.EntryLabel>
                      <St.PercentGroup>
                        <St.PercentField
                          value={discountText}
                          inputMode="numeric"
                          disabled={negotiationLocked}
                          onChange={(event) =>
                            handleDiscountInput(event.target.value)
                          }
                          onBlur={commitDiscountInput}
                        />
                        <St.PercentSuffix>%</St.PercentSuffix>
                      </St.PercentGroup>
                    </St.EntryColumn>
                  </St.EntryRow>
                  {negotiationType === 'avista' ? (
                    <St.InfoNotice>
                      <St.InfoBadge>i</St.InfoBadge>
                      <span>
                        A nova fatura será gerada com o valor da entrada para
                        negociação à vista.
                      </span>
                    </St.InfoNotice>
                  ) : (
                    <>
                      <St.InfoNotice>
                        <St.InfoBadge>i</St.InfoBadge>
                        <span>
                          Após o pagamento da entrada, as parcelas serão
                          lançadas mensalmente.
                        </span>
                      </St.InfoNotice>
                      <St.InstallmentRow>
                        <St.EntryColumn>
                          <St.EntryLabel>Valor da entrada</St.EntryLabel>
                          <St.Amount>{formatMoney(entryValue)}</St.Amount>
                        </St.EntryColumn>
                        <St.EntryColumn>
                          <St.EntryLabel>Número de parcelas</St.EntryLabel>
                          <St.InstallmentField
                            as="select"
                            value={installments}
                            disabled={
                              negotiationLocked ||
                              installmentOptions.length === 0
                            }
                            onChange={(event) =>
                              setInstallments(Number(event.target.value))
                            }
                          >
                            {installmentOptions.length === 0 ? (
                              <option value="">-</option>
                            ) : (
                              installmentOptions.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                  {' '}
                                  {option === 1 ? 'parcela' : 'parcelas'}
                                </option>
                              ))
                            )}
                          </St.InstallmentField>
                        </St.EntryColumn>
                        <St.EntryColumn>
                          <St.EntryLabel>Valor das parcelas</St.EntryLabel>
                          <St.Amount>{formatMoney(installmentValue)}</St.Amount>
                          <St.MutedText>
                            Taxa de juros: 5,9% ao mês
                          </St.MutedText>
                        </St.EntryColumn>
                      </St.InstallmentRow>
                    </>
                  )}
                  <St.NewInvoice>
                    <St.PanelTitle>Nova fatura (Após acordo)</St.PanelTitle>
                    <St.NewInvoiceValue>
                      <St.PlainText>Valor da nova fatura</St.PlainText>
                      <St.DiscountAmount>
                        {formatMoney(newInvoiceValue)}
                      </St.DiscountAmount>
                    </St.NewInvoiceValue>
                  </St.NewInvoice>
                  <div>
                    <St.PanelTitle>Resumo da negociação</St.PanelTitle>
                    <St.ConfigBlock>
                      <St.SummaryLine>
                        <St.EntryLabel>Desconto aplicado</St.EntryLabel>
                        <St.DiscountAmount>
                          - 
                          {' '}
                          {formatMoney(appliedDiscount)}
                        </St.DiscountAmount>
                      </St.SummaryLine>
                      <St.SummaryLine>
                        <St.EntryLabel>Total da dívida</St.EntryLabel>
                        <St.PlainText>{formatMoney(debtTotal)}</St.PlainText>
                      </St.SummaryLine>
                      <St.WarningNotice>
                        <St.WarningBadge>
                          <span>i</span>
                        </St.WarningBadge>
                        <span>
                          O acordo será representado por uma nova fatura que
                          será gerada com o vencimento em até 15 dias
                        </span>
                      </St.WarningNotice>
                      <St.SummaryActions>
                        {pix ? (
                          <Button
                            fullWidth
                            outlined
                            type="button"
                            onClick={() => setPixModalOpen(true)}
                          >
                            <span>Gerar Pix copia e cola</span>
                          </Button>
                        ) : (
                          <>
                            <input
                              ref={audioInputRef}
                              type="file"
                              accept="audio/wav, audio/wave, audio/x-wav"
                              style={{ display: 'none' }}
                              onChange={(event) => {
                                const nextFile =
                                  event.target.files?.[0] ?? null;
                                setAudioFile(nextFile);
                              }}
                            />
                            <St.EntryLabel>Áudio da negociação *</St.EntryLabel>
                            <Button
                              fullWidth
                              outlined
                              type="button"
                              onClick={() => audioInputRef.current?.click()}
                            >
                              <span>Subir arquivo</span>
                            </Button>
                            {audioFile && (
                              <St.EntryLabel>{audioFile.name}</St.EntryLabel>
                            )}
                            {showRequired && !audioFile && (
                              <St.RequiredError>
                                Selecione o arquivo de áudio da negociação.
                              </St.RequiredError>
                            )}
                            <Button
                              fullWidth
                              type="button"
                              loading={creatingNegotiation}
                              onClick={handleCreateNegotiation}
                            >
                              <span>Confirmar negociação</span>
                            </Button>
                          </>
                        )}
                        <Button
                          fullWidth
                          type="button"
                          onClick={() =>
                            history.push('/negociacao-dividas/buscar-cliente')
                          }
                        >
                          <span>Voltar</span>
                        </Button>
                      </St.SummaryActions>
                    </St.ConfigBlock>
                  </div>
                </St.ConfigBlock>
              </div>
            </St.ConfigStack>
          </St.Panel>
        </St.SideColumn>
      </St.Boxes>
    </Container>
  );
};

export default DebtNegotiationClient;
